export const MIN_SEARCH_LENGTH = 3;
export const FULLTEXT_OPERATORS = [
    "+",
    "-",
    ">",
    "<",
    "(",
    ")",
    "~",
    "*",
    '"',
    "@",
];
export function fulltextOperators(term) {
    const found = FULLTEXT_OPERATORS.filter((op) => term.includes(op));
    return [...new Set(found)];
}
export function searchTerms(query) {
    return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}
export const ASSET_SORTS = [
    "created",
    "updated",
    "title",
    "relevance",
];
export const SORT_ORDERS = ["asc", "desc"];
export function isAssetSort(value) {
    return (typeof value === "string" &&
        ASSET_SORTS.includes(value));
}
export function isSortOrder(value) {
    return (typeof value === "string" &&
        SORT_ORDERS.includes(value));
}
export const DEFAULT_ASSET_SORT = "created";
export const DEFAULT_SORT_ORDER = "desc";
export function needsCompleteSweep(sort, order, searching) {
    if (sort === "relevance")
        return false;
    if (sort === "created" && order === "desc" && !searching)
        return false;
    return true;
}
export const ASSET_SORT_LOCALE = "sv";
let collator = null;
export function assetTitleCollator() {
    if (!collator) {
        collator = new Intl.Collator(ASSET_SORT_LOCALE, { numeric: true });
    }
    return collator;
}
export function displayTitle(asset, language) {
    var _b, _c, _d;
    if (language) {
        const lang = language.trim().toLowerCase();
        const localised = ((_b = asset.localizedInfo) !== null && _b !== void 0 ? _b : []).find((row) => { var _b; return ((_b = row.language) === null || _b === void 0 ? void 0 : _b.trim().toLowerCase()) === lang; });
        if ((_c = localised === null || localised === void 0 ? void 0 : localised.title) === null || _c === void 0 ? void 0 : _c.trim())
            return localised.title;
    }
    return (_d = asset.originalTitle) !== null && _d !== void 0 ? _d : "";
}
function timestamp(value) {
    const parsed = Date.parse(value !== null && value !== void 0 ? value : "");
    return Number.isFinite(parsed) ? parsed : 0;
}
export function sortAssets(rows, sort, order, opts = {}) {
    const compare = assetComparator(sort, opts);
    const direction = order === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
        const result = compare(a, b) * direction;
        if (result !== 0)
            return result;
        return a.assetId < b.assetId ? -1 : a.assetId > b.assetId ? 1 : 0;
    });
}
function assetComparator(sort, opts) {
    if (sort === "created") {
        return (a, b) => timestamp(a.createdAt) - timestamp(b.createdAt);
    }
    if (sort === "updated") {
        return (a, b) => timestamp(a.updatedAt) - timestamp(b.updatedAt);
    }
    const collate = assetTitleCollator();
    return (a, b) => collate.compare(displayTitle(a, opts.language), displayTitle(b, opts.language));
}
const MATCH_SOURCES = [
    {
        field: "originalTitle",
        label: "Original title",
        read: (a) => [a.originalTitle],
    },
    {
        field: "localizedTitle",
        label: "Title",
        read: (a) => { var _b; return ((_b = a.localizedInfo) !== null && _b !== void 0 ? _b : []).map((row) => row.title); },
        language: (a, value) => { var _b, _c; return (_c = ((_b = a.localizedInfo) !== null && _b !== void 0 ? _b : []).find((row) => row.title === value)) === null || _c === void 0 ? void 0 : _c.language; },
    },
    { field: "assetId", label: "Asset ID", read: (a) => [a.assetId] },
    {
        field: "reference",
        label: "Reference",
        read: (_a, ctx) => { var _b; return [(_b = ctx.reference) !== null && _b !== void 0 ? _b : undefined]; },
    },
    { field: "externalId", label: "External ID", read: (a) => [a.externalId] },
    {
        field: "provider",
        label: "Provider",
        read: (a) => { var _b; return [(_b = a.provider) === null || _b === void 0 ? void 0 : _b.name]; },
        keyed: (a) => {
            var _b;
            return ((_b = a.provider) === null || _b === void 0 ? void 0 : _b.providerId)
                ? [
                    {
                        key: a.provider.providerId,
                        shown: a.provider.name || a.provider.providerId,
                    },
                ]
                : [];
        },
    },
    { field: "studio", label: "Studio", read: (a) => [a.studio] },
    {
        field: "genre",
        label: "Genre",
        read: (a) => {
            var _b;
            return ((_b = a.genres) !== null && _b !== void 0 ? _b : []).flatMap((g) => {
                var _b;
                return [
                    g.name,
                    ...((_b = g.localizedName) !== null && _b !== void 0 ? _b : []).map((l) => l.name),
                ];
            });
        },
        keyed: (a) => {
            var _b;
            return ((_b = a.genres) !== null && _b !== void 0 ? _b : []).map((g) => ({
                key: g.genreId,
                shown: g.name || g.genreId,
            }));
        },
    },
    {
        field: "tag",
        label: "Tag",
        read: (a) => { var _b; return ((_b = a.tags) !== null && _b !== void 0 ? _b : []).map((t) => t.name); },
        keyed: (a) => { var _b; return ((_b = a.tags) !== null && _b !== void 0 ? _b : []).map((t) => ({ key: t.tagId, shown: t.name || t.tagId })); },
    },
    { field: "keyword", label: "Keyword", read: (a) => { var _b; return (_b = a.keywords) !== null && _b !== void 0 ? _b : []; } },
    {
        field: "contributor",
        label: "Contributor",
        read: (a) => { var _b; return ((_b = a.contributors) !== null && _b !== void 0 ? _b : []).map((c) => c.fullName); },
    },
    {
        field: "countryOfOrigin",
        label: "Country",
        read: (a) => [a.countryOfOrigin],
    },
    { field: "seriesId", label: "Series ID", read: (a) => [a.seriesId] },
    { field: "seasonId", label: "Season ID", read: (a) => [a.seasonId] },
];
function hit(values, term) {
    for (const value of values) {
        if (typeof value !== "string" || !value)
            continue;
        if (value.toLowerCase().includes(term))
            return value;
    }
    return null;
}
function keyHit(keyed, value) {
    const match = keyed.find((entry) => entry.key.toLowerCase() === value);
    return match ? match.shown : null;
}
export function assetMatchReasons(asset, terms, ctx = {}) {
    var _b, _c;
    const matches = [];
    const add = (source, value) => {
        var _b;
        if (matches.some((m) => m.field === source.field && m.value === value))
            return;
        const language = (_b = source.language) === null || _b === void 0 ? void 0 : _b.call(source, asset, value);
        matches.push({
            field: source.field,
            label: source.label,
            value,
            ...(language ? { language } : {}),
        });
    };
    for (const term of terms) {
        for (const source of MATCH_SOURCES) {
            const value = hit(source.read(asset, ctx), term);
            if (!value)
                continue;
            add(source, value);
            break;
        }
    }
    for (const value of (_b = ctx.exact) !== null && _b !== void 0 ? _b : []) {
        const needle = value.trim().toLowerCase();
        if (!needle)
            continue;
        for (const source of MATCH_SOURCES) {
            const shown = (_c = (source.keyed ? keyHit(source.keyed(asset), needle) : null)) !== null && _c !== void 0 ? _c : source
                .read(asset, ctx)
                .find((v) => typeof v === "string" && v.toLowerCase() === needle);
            if (shown) {
                add(source, shown);
                break;
            }
        }
    }
    return matches;
}
export function formatMatchReasons(matches, visibleFields = []) {
    const captions = matches
        .filter((m) => !visibleFields.includes(m.field))
        .map((m) => m.language
        ? `${m.label} (${m.language}): ${m.value}`
        : `${m.label}: ${m.value}`);
    return captions.length ? [...new Set(captions)].join(" · ") : null;
}
